import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";

import { HeroFeature } from "@/components/site/HeroFeature";
import { MeetingCard } from "@/components/site/MeetingCard";
import { PageHero } from "@/components/site/PageHero";
import euParliament from "@/assets/eu-parliament.webp.asset.json";
import { meetingsQuery } from "@/lib/content";
import { orderCategories } from "@/lib/meeting-format";
import { useCompleteRows } from "@/lib/use-complete-rows";
import { LoadMoreButton } from "@/components/site/LoadMoreButton";
import { useAuth0 } from "@/integrations/fc/auth0";

const GRID_BREAKPOINTS = [[640, 2], [1024, 3]] as const;

export const Route = createFileRoute("/meetings/")({
  staticData: { sitemap: true },
  validateSearch: (search: Record<string, unknown>): { more?: 1 } =>
    search["more"] === 1 || search["more"] === "1" ? { more: 1 } : {},
  loader: ({ context }) => context.queryClient.ensureQueryData(meetingsQuery()),
  head: () => ({
    meta: [
      { title: "Meetings & Briefings — Foreign Council On Economic Relations" },
      {
        name: "description",
        content:
          "Diplomatic briefings, induction seminars, and executive roundtables connecting the diplomatic corps, government leaders, and international industry.",
      },
      { property: "og:title", content: "Meetings & Briefings — Foreign Council On Economic Relations" },
      {
        property: "og:description",
        content:
          "Diplomatic briefings, induction seminars, and executive roundtables connecting the diplomatic corps, government leaders, and international industry.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://foreign-council.org/meetings" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://foreign-council.org/meetings" }],
  }),
  component: MeetingsPage,
});

function MeetingsPage() {
  const { data: meetings } = useSuspenseQuery(meetingsQuery());
  const [active, setActive] = useState<string>("All");

  const upcoming = meetings.find((m) => m.is_upcoming);

  const categories = useMemo(
    () => ["All", ...orderCategories(Array.from(new Set(meetings.map((m) => m.category))))],
    [meetings],
  );

  const filtered = active === "All" ? meetings : meetings.filter((m) => m.category === active);
  const { isAuthenticated } = useAuth0();
  const { more } = Route.useSearch();
  const [showAll, setShowAll] = useState(more === 1);
  const rowCount = useCompleteRows(filtered.length, GRID_BREAKPOINTS);
  // Members may reveal events trimmed to keep rows full; guests keep the trimmed grid.
  const visible = isAuthenticated && showAll ? filtered : filtered.slice(0, rowCount);
  const canLoadMore = isAuthenticated && !showAll && visible.length < filtered.length;

  return (
    <>
      {upcoming ? (
        <HeroFeature meeting={upcoming} priority />
      ) : (
        <PageHero
          image={euParliament.url}
          alt="Meetings in Berlin"
        />
      )}
      <section className="bg-surface">
      <div className="mx-auto w-full max-w-[1400px] px-5 py-16 lg:px-8 lg:py-20">
        <h1 className="fc-h2 fc-rule text-foreground">Meetings</h1>


        <div className="mt-10 flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category}
              type="button"
              onClick={() => {
                setActive(category);
                setShowAll(false);
              }}
              className={`fc-eyebrow px-3 py-2 transition-colors ${
                active === category
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-foreground hover:bg-primary hover:text-primary-foreground"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((meeting) => (
            <MeetingCard key={meeting.id} meeting={meeting} />
          ))}
        </div>
        {canLoadMore && <LoadMoreButton onClick={() => setShowAll(true)} />}
      </div>
    </section>
    </>
  );
}
